// The WebSocket protocol between a client and a server's ServerRoom Durable Object.
// One socket per server carries everything: messages, acks, typing, presence, history, reactions, threads, and apps
// (commands, actions, live app data, private cards; see apps.ts and docs/apps.md).
import type { AppEvent, AppManifest, AppRecord, Card, PrivateCard } from "./apps.ts";
import type { AppVersion, BloomingThread, BloomWho } from "./blooming.ts";
import type { ListInfo, ListItem, SystemEvent } from "./lists.ts";
import type { IntegrationSetting } from "./integrations.ts";
import type { BotAccess, BotInfo } from "./bots.ts";
//
import type { CustomEmoji, Sticker, StickerFormat } from "./emoji.ts";
import type { MuteFor, ServerNotify } from "./notify.ts";

// Streams: every message lives in a stream with its own gap-free sequence numbers. A channel's stream id is the
// channel id; a thread's stream id is "t:" + the id of the message it hangs off (see threadStreamId).
import type { FileVariant } from "./media.ts";
import type { SearchFilters } from "./search.ts";

export type Role = "owner" | "admin" | "member";

/** What everyone sees of a person: their display name and avatar URL (null: coloured initials), and their name color. */
export interface MemberProfile {
  id: string;
  name: string;
  image: string | null;
  /** The #rrggbb of their stored name color (#631, #698). A server older than #631 leaves it out. */
  nameColor?: string;
}

export interface Category {
  id: string;
  name: string;
  position: number;
}

export interface Channel {
  id: string;
  name: string;
  topic: string;
  /** null: not in a category (listed above all categories, as in Discord). */
  categoryId: string | null;
  /** Order within its category. */
  position: number;
  /** Sequence number of the newest message; 0 when the channel is empty. */
  lastSeq: number;
  /**
   * "text": a normal channel everyone in the server sees. "dm": a direct message between `members` (2–10 people
   * in this server); only they receive it, see it in `ready`, or can read/post. DMs have no category; `name` is ""
   * for a DM (clients show the other members' names).
   */
  kind: "text" | "dm";
  /** DMs only: the user ids in the conversation (including you), sorted. */
  members?: string[];
  /** Text channels: apps whose cards are turned off here (Channel settings → Apps). Absent: all on. */
  appCardsOff?: string[];
  /**
   * Text channels: messages in it and its threads in the past week (`ACTIVITY_WINDOW_MS`), quiet lines not counted,
   * as of this `ready` or `structure`; clients cap "new since my last read" with it (`newSinceRead`, #59).
   */
  weekMessages?: number;
  /** Set by clients, never sent: this channel's `lastSeq` when `weekMessages` was counted (`newSinceRead`). */
  weekSeq?: number;
}

/** How loudly a channel or DM notifies one person. Defaults: DMs "all", channels "mentions". */
export type NotifyLevel = "all" | "mentions" | "none";

/**
 * What a photo's EXIF says, as the server stores it (#379): checked values from the allowlist block only (shared/src/exifAllow.ts,
 * `photoOf`), and only when it has a camera, a lens or a setting. Every field is optional.
 */
export interface PhotoInfo {
  /** The camera's model, with its make when the model does not say it: "iPhone 18 Pro", "Leica M11-P". Printable ASCII, at most 64. */
  camera?: string;
  /** The lens's name, as the file says it. */
  lens?: string;
  /** When the photo was taken, as the camera's own clock showed it: "2026-10-28T06:44:52". Not a UTC time. */
  takenAt?: string;
  /** The time zone of `takenAt` ("-04:00"), when the camera recorded one. */
  offset?: string;
  /** The f-number: 1.8994 is f/1.9. */
  fNumber?: number;
  /** The exposure time in seconds. */
  exposure?: number;
  iso?: number;
  /** The focal length in mm. */
  focal?: number;
  /** The 35 mm equivalent focal length. */
  focal35?: number;
}

/** A file in R2 (uploaded through /api/servers/:id/files) or a GIF from the GIF provider. */
export type Attachment =
  | {
      kind: "file";
      /** R2 key, always under servers/<serverId>/. Served at /api/files/<key>. */
      key: string;
      name: string;
      /** MIME type as uploaded. */
      type: string;
      size: number;
      /** Images and videos: measured on the uploader's device, so the layout never shifts while loading. */
      width?: number;
      height?: number;
      /** Images and videos: drawn blurred until each viewer chooses to look (like a Discord spoiler). */
      private?: boolean;
      /** Smaller copies the server made after upload (see media.ts), served at /api/files/<key>/<name>. Set by the
       *  server only; they may arrive after the message does. */
      variants?: FileVariant[];
      /** Pictures: the camera, lens, time and shot settings from the photo's EXIF, read once by the server when the upload is
       *  cleaned (#379). Set by the server only; a client never reads EXIF. Absent on old uploads and on a picture with none.
       *  Untrusted, as the file is: draw it through `formatPhotoInfo`. */
      photo?: PhotoInfo;
    }
  | {
      kind: "gif";
      provider: "klipy";
      /** Provider's id/slug, for attribution links. */
      id: string;
      /** Animated image or video to show in chat. */
      url: string;
      /** "image" (webp/gif) or "video" (mp4, looped and muted). */
      format: "image" | "video";
      width: number;
      height: number;
      title?: string;
      /** Drawn blurred until each viewer chooses to look. */
      private?: boolean;
    }
  | {
      /** One of the server's stickers (see emoji.ts), served at /api/servers/<serverId>/stickers/<id>. */
      kind: "sticker";
      id: string;
      /** As it was when sent: shown if the sticker is later deleted. */
      name: string;
      format: StickerFormat;
      private?: never;
    };

/** Names one attachment within its message: the R2 key of a file, the URL of a GIF. */
export const attachmentRef = (a: Attachment) => (a.kind === "file" ? a.key : a.kind === "sticker" ? `sticker:${a.id}` : a.url);

/** Pictures, videos and GIFs: the attachments drawn inline (same types as web/src/upload.ts), so the ones that can be private. */
export const isVisualAttachment = (a: Attachment) => a.kind === "gif" || (a.kind === "file" && /^(image\/(png|jpeg|gif|webp|avif)|video\/(mp4|webm|quicktime))$/.test(a.type));

/**
 * A link preview, made by the server when the message is sent (see worker/src/unfurl.ts). Image and thumbnail URLs
 * point at our own image proxy (/api/img?...), so they keep working and don't leak readers' IPs to other sites.
 */
export interface Embed {
  /** The link as it appeared in the message. */
  url: string;
  /** Which source produced it, e.g. "x", "instagram", "youtube", "reddit", "tiktok", "og", "image" (the picture alone, #688). */
  provider: string;
  /** Shown above the title: "X", "YouTube", "nytimes.com"… */
  site: string;
  siteIcon?: string;
  title?: string;
  description?: string;
  author?: { name: string; handle?: string; avatar?: string; url?: string };
  /**
   * Main picture (or a video's poster). `type` is the file's content type, set on an `image` embed (a link straight to a
   * picture, #688): a client plays `image/gif` as it plays an uploaded GIF.
   */
  image?: { url: string; width?: number; height?: number; type?: string };
  /** More pictures (a tweet with several photos). */
  gallery?: { url: string; width?: number; height?: number }[];
  /** A playable video: a direct file (mp4) or a player page to load in an iframe after a click. */
  video?: { url?: string; iframe?: string; width?: number; height?: number; poster?: string };
  /** Server time the post was made, when the source says. */
  ts?: number;
  /** Little counters under a post, e.g. { likes: 120, replies: 4 }. */
  stats?: Record<string, number>;
  /** Accent color for the card's edge, "#rrggbb". */
  color?: string;
  /** A post this one quotes (a quote tweet), shown as a card inside the card, with its own pictures or video. */
  quote?: Embed;
}

/** The message being quoted by a reply, as it was when the reply was sent. */
export interface ReplyRef {
  id: string;
  authorId: string;
  /** First 140 characters of the quoted message. */
  excerpt: string;
  /** The quoted message had attachments (shown as "image" / "attachment" when there's no text). */
  attachments: number;
}

export interface ThreadSummary {
  /** Number of replies in the thread. */
  count: number;
  /** Server time of the newest reply. */
  lastTs: number;
  /** Up to 3 distinct recent repliers, newest first. */
  participants: string[];
}

export interface Message {
  /** ULID made on the sender's device, so an optimistic message and its confirmation share an id. */
  id: string;
  /** The stream: a channel id, or "t:<parent message id>" for a thread reply. */
  channelId: string;
  /** Position in the stream, assigned by the room: 1, 2, 3… with no gaps. Order by this, never by id or time. */
  seq: number;
  authorId: string;
  /** May be empty when there are attachments. Channel references are stored as "<#channelId>" (see channels.ts). */
  body: string;
  /** Server time, Unix ms. */
  ts: number;
  editedAt?: number;
  attachments?: Attachment[];
  replyTo?: ReplyRef;
  /** emoji → user ids who reacted with it, in the order they reacted. */
  reactions?: Record<string, string[]>;
  /**
   * Jumbo reactions (#714): emoji → the user ids whose reaction is jumbo, in the order they reacted. Always a subset of
   * `reactions[emoji]` (the same key, the same people), so a client that doesn't know this field draws a normal counter
   * with the right count. A counter is jumbo while at least one reaction on it is. Absent: none is.
   */
  jumbo?: Record<string, string[]>;
  /** Present on a channel message that has a thread under it. */
  thread?: ThreadSummary;
  /** Link previews, filled in by the room a moment after sending (an "embeds" event). */
  embeds?: Embed[];
  /** Deleted: body and attachments are gone. Kept only so sequence numbers stay gap-free (and a thread keeps its parent). */
  deleted?: boolean;
  /**
   * User ids @mentioned, worked out by the room from "<@userId>" tokens in the body (see mentions.ts) when the
   * message is sent or edited. "@everyone" in a channel adds "everyone".
   */
  mentions?: string[];
  /**
   * @Bloom is still writing this reply (authorId BLOOM_ID): `body` is what has arrived so far, and "stream" events
   * bring more. Cleared when the reply is finished (the last "stream" event has `done`).
   */
  streaming?: boolean;
  /**
   * Sent by the author's own AI over the connector (docs/connector.md "Messages"): its name ("Claude Code"). Clients
   * show it next to the author's name, and a message with a different `via` starts a new group.
   */
  via?: string;
  /**
   * A card an app posted (authorId: the person who used the app). `body` holds a plain-text version of it, for
   * push, @Bloom and search. Card messages can't be edited; they can be deleted, reacted to and threaded like any.
   * @Bloom's replies can carry one too (a card from an app tool it used), under its text; `body` is then the text.
   */
  card?: Card;
  /**
   * The shared lists this message is in (pins included), by list id. Everyone who can see the message can see these
   * lists. Private lists are never here (see ListInfo and ready.privateItems).
   */
  lists?: string[];
  /** A quiet line about something someone did (pinned a message, added it to lists), not something they wrote. */
  system?: SystemEvent;
  /**
   * A forwarded message (Discord's Forward): a copy of another message in this server, taken when it was forwarded.
   * `body` is then the forwarder's own optional note (it may @mention people; the copy never notifies anyone).
   */
  forward?: Forward;
}

/**
 * What a forward shows of the original, snapshotted at forward time: later edits or deletion of the original don't
 * change it. Its files are copies the forward owns (new keys, downloadable by whoever can see the forward), so
 * deleting either message never breaks the other. See docs/forwarding.md.
 */
export interface Forward {
  /** The original's conversation: a text channel or a DM (for a thread reply, the channel the thread is in). */
  channelId: string;
  /** The original's stream (channelId, or "t:<parent id>" for a thread reply). */
  stream: string;
  messageId: string;
  /** The original was in a DM (or a thread under a DM message): only its members can follow the link back. */
  dm?: true;
  /** When the original was posted (server time). */
  ts: number;
  /** The original's text as stored (tokens kept, so names follow renames). For an app card, its plain-text body. */
  body: string;
  attachments?: Attachment[];
  embeds?: Embed[];
}
/** Most conversations one message can be forwarded to at once (the picker's limit; each is its own send). */
export const MAX_FORWARD_TARGETS = 5;

/** A message found by search: where it lives (its stream, and the channel or DM that stream belongs to). */
export interface SearchHit {
  messageId: string;
  channelId: string;
  rootId: string;
}
/** Search: at most this many words, each at least this long, and this many hits a page. */
export const SEARCH_MAX_WORDS = 8;
export const SEARCH_PAGE = 25;

export const threadStreamId = (parentId: string) => `t:${parentId}`;
export const isThreadStream = (streamId: string) => streamId.startsWith("t:");
export const threadParentId = (streamId: string) => streamId.slice(2);

export const MAX_MESSAGE_LENGTH = 4000;
export const MAX_ATTACHMENTS = 10;
/** Largest file anyone can upload (enforced by the Worker across every part of a multipart upload). */
export const MAX_FILE_BYTES = 100 * 1024 * 1024;
/**
 * Largest file sent in one request (PUT /api/servers/:id/files). Bigger ones go up in parts (R2 multipart, see
 * worker/src/uploads.ts): one request body is capped at 100 MB on Cloudflare's Free and Pro plans, and the Worker
 * would have to hold all of it in its 128 MB of memory.
 */
export const SINGLE_UPLOAD_MAX_BYTES = 25 * 1024 * 1024;
/** Size of each part of a multipart upload but the last (R2 needs at least 5 MiB, and equal sizes). */
export const UPLOAD_PART_BYTES = 8 * 1024 * 1024;
/** Distinct emoji per message, like Discord. */
export const MAX_REACTIONS = 20;

/**
 * Super reactions (#87): a new reaction can arrive with a short effect, played once by every device that has the
 * message on screen. The room picks it; it's in the live event only, never stored, so history and `ready` are plain.
 */
export const REACTION_EFFECTS = ["zoom", "spin", "poof"] as const;
export type ReactionEffect = (typeof REACTION_EFFECTS)[number];
export const isReactionEffect = (v: unknown): v is ReactionEffect => typeof v === "string" && (REACTION_EFFECTS as readonly string[]).includes(v);
/** Clients send typing at most this often; the room forgets it after TYPING_TTL_MS. */
export const TYPING_THROTTLE_MS = 3000;
export const TYPING_TTL_MS = 8000;
/** Channel topics, shown under the channel name. */
export const MAX_TOPIC_LENGTH = 200;
/** Messages per backfill or history page. */
export const PAGE_SIZE = 50;
/** Most messages one ranged history request (afterSeq / toId) returns; a client wanting more asks again. */
export const HISTORY_MAX = 500;
/**
 * Most changed messages the room re-sends for one channel on reconnect (see hello's `rev`). With more than this, the
 * room sends the newest page as a fresh start instead (backfill with `more`), as if the client had fallen behind.
 */
export const CHANGED_MAX = 200;
/** Most records one appQuery returns. */
export const MAX_APP_RECORDS = 1000;
/** App events per history page. */
export const APP_HISTORY_PAGE = 50;

// Client → room
export type ClientEvent =
  /**
   * First frame after connecting. cursors: the newest seq the client already has, per channel.
   * rev: the `rev` of the last "ready" this client saw (kept with its cached messages). The room then also re-sends
   * messages at or before each cursor that changed since (edits, deletes, reactions, previews, thread replies), as
   * "changed". Omitted: the client holds nothing older than this connection, so it needs no changes.
   * focus: the channel on screen, caught up first.
   */
  | { t: "hello"; cursors: Record<string, number>; rev?: number; focus?: string }
  /**
   * channelId: a channel, or a thread stream ("t:<parent id>", which starts the thread if it's new).
   * replyTo: id of a message in the same stream to quote.
   */
  /** lists: list ids (or PINS) to add the new message to, as it's posted (lists.ts). */
  /**
   * forward: the id of a message to forward here (a text channel or DM, not a thread). The room snapshots it (see
   * Forward) and `body` is an optional note; no attachments, replyTo or lists with it.
   */
  | { t: "send"; id: string; channelId: string; body: string; replyTo?: string; attachments?: Attachment[]; lists?: string[]; forward?: string }
  /**
   * effect: a super reaction (#87). "random" lets the room pick; a named one is kept. Anything else, a reaction already
   * on the message, or `on: false` makes it plain.
   * jumbo: a jumbo reaction (#714), with `on: true`. It makes my reaction jumbo, also one I already have (the count
   * stays); it is stored, and it replaces `effect`. Absent, or anything but `true`, is a normal reaction: it never
   * makes a jumbo one normal again (take it back, then add it).
   */
  | { t: "react"; channelId: string; messageId: string; emoji: string; on: boolean; effect?: ReactionEffect | "random"; jumbo?: boolean }
  /** Change the text of your own message. */
  | { t: "edit"; channelId: string; messageId: string; body: string }
  /** Delete a message: your own, or anyone's if you're an owner or admin. */
  | { t: "delete"; channelId: string; messageId: string }
  /**
   * Remove a message's link previews for everyone: your own message, or anyone's if you're an owner or admin. The
   * room answers everyone with an "embeds" event carrying [], and later edits of the message make no new previews.
   */
  | { t: "suppressEmbeds"; channelId: string; messageId: string }
  /**
   * Remove one attachment (named by attachmentRef) from a message for everyone: your own message, or anyone's if
   * you're an owner or admin. Its file is deleted for good. A message left with no text and no attachments is deleted.
   */
  | { t: "removeAttachment"; channelId: string; messageId: string; ref: string }
  /** Mark a picture, video or GIF in your own message private (blurred until each viewer looks), or not. */
  | { t: "setAttachmentPrivate"; channelId: string; messageId: string; ref: string; private: boolean }
  | { t: "typing"; channelId: string }
  /**
   * Older messages before `beforeSeq` (0 or omitted: the newest page). Works for threads too.
   * afterSeq: instead of one page, everything after this seq (and before `beforeSeq`), up to HISTORY_MAX: the newest
   * of them if there are more, so the reply always joins up with what the client holds. Used to reach the first
   * unread message. toId: the same, back to a few messages before this message (in this stream), to jump to a quote.
   */
  | { t: "history"; rid: string; channelId: string; beforeSeq?: number; afterSeq?: number; toId?: string }
  | { t: "createChannel"; rid: string; name: string; categoryId?: string | null }
  | { t: "createCategory"; rid: string; name: string }
  /** Owners and admins. The room answers with a "channel" event carrying the new name. */
  | { t: "renameChannel"; rid: string; channelId: string; name: string }
  // Channel and category settings (owners and admins; never DMs). Every change reaches everyone connected.
  /** Up to MAX_TOPIC_LENGTH characters; "" clears it. Answered with a "channel" event. */
  | { t: "setTopic"; rid: string; channelId: string; topic: string }
  /** Owners and admins: allow (or stop) one app's cards in a text channel. Answered with "channel". */
  | { t: "setAppCards"; rid: string; channelId: string; app: string; on: boolean }
  /** Deletes a channel with all its messages, threads, reactions, read state, notification levels and files. */
  | { t: "deleteChannel"; rid: string; channelId: string }
  /**
   * Puts a channel in `categoryId` (null: uncategorised) at index `position` among that category's channels (past
   * the end: last). Answered with a "structure" event.
   */
  | { t: "moveChannel"; rid: string; channelId: string; categoryId: string | null; position: number }
  /** Answered with a "category" event. */
  | { t: "renameCategory"; rid: string; categoryId: string; name: string }
  /** Moves a category to index `position` among the categories. Answered with a "structure" event. */
  | { t: "moveCategory"; rid: string; categoryId: string; position: number }
  /** Deletes a category; its channels stay, uncategorised (after the ones already there). */
  | { t: "deleteCategory"; rid: string; categoryId: string }
  /** Latency probe; the room echoes `n` straight back. */
  | { t: "ping"; n: number }
  /** Start (or find) the DM with these people (not counting yourself). The room answers with a "channel" event. */
  | { t: "openDm"; rid: string; userIds: string[] }
  /**
   * Look up one message by id, for a message link (opening it, or its preview). Answered with "messageRef": the
   * message only if I can see where it is and it's still there.
   */
  | { t: "messageRef"; rid: string; messageId: string }
  /** I've read `channelId` (a channel or DM) up to `seq`. Synced to my other devices; never moves backwards. */
  | { t: "read"; channelId: string; seq: number }
  /**
   * Mark unread from a message (Discord's "Mark Unread"): my read position for `channelId` (a channel or DM, not a
   * thread) becomes `seq` − 1, so the message numbered `seq` is the first unread. Unlike "read", it can move back.
   */
  | { t: "unread"; channelId: string; seq: number }
  /**
   * This device is (or stopped being) in front of me: focused and visible. Push skips people with an active device.
   * A desktop client (web, Mac) adds `lastInputAt` (Unix ms, its clock): the last key, pointer or touch. It sends the
   * frame when its state changes and, while there is input, at most every IDLE_REPORT_MS. The room then counts the
   * device as in use only while that input is newer than my idle-push setting, and holds my phone's push until then
   * (shared/src/idlePush.ts, #683). Without `lastInputAt` the device is active for as long as it says so.
   */
  | { t: "active"; active: boolean; lastInputAt?: number }
  /** My notification level for a channel or DM; null: follow the server's (DMs: all). */
  | { t: "notifyPref"; channelId: string; level: NotifyLevel | null }
  /** Close a DM in my sidebar (#131): hidden until a message after `seq` (its lastSeq when closed); null opens it again. */
  | { t: "dmClose"; channelId: string; seq: number | null }
  /** Mute a channel or DM (`channelId`) or the whole server (no `channelId`; DMs aren't covered) for a while or for good;
   *  `for: null` unmutes. The room works out when it ends, from its own clock. */
  | { t: "notifyMute"; channelId?: string; for: MuteFor | null }
  /** My settings for this whole server: the level its channels follow, and suppressing @everyone. */
  | { t: "serverNotify"; level?: NotifyLevel; suppressEveryone?: boolean }
  // ---- Apps (docs/apps.md) ----
  /**
   * Run a slash command in `channelId` (a channel, DM or thread stream). `args`: inline arguments and free text by
   * input key, as parseCommand read them. Answered with "commandResult"; a form comes as a "privateCard", a posted
   * card as a "msg".
   */
  | { t: "command"; rid: string; channelId: string; app: string; command: string; args: Record<string, unknown> }
  /**
   * Call an app's action. `channelId`: where the call happens (cards it posts go there). `cardId`: the private card
   * whose form this submits (it turns into its "done" state). Answered with "appResult".
   */
  | { t: "appCall"; rid: string; app: string; action: string; input: Record<string, unknown>; channelId?: string; cardId?: string }
  /** Every record of one of an app's collections (up to MAX_APP_RECORDS). Answered with "appRecords". */
  | { t: "appQuery"; rid: string; app: string; collection: string }
  /** Hear about changes ("appChange") to an app's records: all of them (no collection), or one collection's. */
  | { t: "appSub"; app: string; collection?: string; on: boolean }
  /** An app's event log, newest first, before event id `before`. Answered with "appHistory". */
  | { t: "appHistory"; rid: string; app: string; before?: number }
  /** Owners and admins: put a record back to its version at `eventId` (itself logged as a new event). */
  | { t: "appRestore"; rid: string; app: string; eventId: number }
  /** A card an app's full view asks to post (data or result kinds), in `channelId`, by me via the app. */
  | { t: "appPostCard"; rid: string; app: string; channelId: string; card: Card }
  /** Throw away one of my private cards. */
  | { t: "dismissCard"; id: string }
  /**
   * An app's full view asks for outside data (docs/integrations.md), for me. Checked by the room against the
   * catalog, the server's settings and the app's manifest. Answered with "integrationResult".
   */
  | { t: "integrationCall"; rid: string; app: string; integration: string; endpoint: string; params: Record<string, unknown> }
  // ---- Lists and pins (lists.ts) ----
  /** Make a list: shared with the server, or private to me. Answered with a "list" event. */
  | { t: "createList"; rid: string; name: string; emoji?: string; visibility: "server" | "private" }
  /**
   * Rename a list, change its emoji, or (shared lists) who may add to it (its creator, or an owner or admin; mine
   * only, if private). Not pins. `adders`: with addPolicy "chosen", the people besides the creator who may add.
   */
  | { t: "updateList"; rid: string; listId: string; name?: string; emoji?: string; addPolicy?: "everyone" | "chosen"; adders?: string[] }
  /** Delete a list and its items (the messages stay). Who may: as for updateList. Not pins. */
  | { t: "deleteList"; rid: string; listId: string }
  /**
   * Add a message to a list, or take it out. `listId`: a list's id, or PINS for the message's own channel's pins.
   * Anyone may pin; adding to a shared list follows its addPolicy; taking out is for whoever added it, the list's
   * creator, or an owner or admin (canAddTo / canRemoveFrom in lists.ts); private lists are their owner's. A DM's
   * messages can go in its pins and in private lists, never in shared lists. Answered with "listItem" (or an error
   * with this rid).
   */
  | { t: "listItem"; rid?: string; listId: string; messageId: string; on: boolean }
  /** A page of a list, newest added first, added before `before` (an addedAt). Answered with "listItems". */
  | { t: "listItems"; rid: string; listId: string; before?: number }
  /**
   * Messages whose text has every word of `q` (case-insensitive), newest first, sent before `before` (a ts, the page
   * cursor), in what I can see; `channelId` narrows it to one channel or DM (and its threads). `filters` (search.ts,
   * Discord's from:/has:/in:/…) narrow it further; with filters, `q` may be empty. Answered with "searchResults".
   */
  | { t: "search"; rid: string; q: string; channelId?: string; before?: number; filters?: SearchFilters }
  // ---- Blooming (docs/blooming.md). Each is answered with "bloomResult" (or "bloomVersions"). ----
  /**
   * Start a blooming thread in a text channel: Bloom posts a message there and the thread hangs off it. `prompt`
   * (optional) is the first thing asked of the build agent. result: { threadId }.
   */
  /**
   * `private`: a private bot, bloomed in a thread in my DM with it (`channelId` is then ignored; only I see the thread,
   * docs/bots.md). result: { threadId }.
   */
  | { t: "bloomStart"; rid: string; channelId: string; prompt: string; bot?: boolean }
  /** The thread's versions, newest first. */
  | { t: "bloomVersions"; rid: string; threadId: string }
  /** The app's owner and editors: make version `version` live (first publish, a new version, or a rollback). */
  | { t: "bloomPublish"; rid: string; threadId: string; version: number }
  /** The owner (or an editor): only owner and editors may prompt while locked. */
  | { t: "bloomLock"; rid: string; threadId: string; locked: boolean }
  /** Show another version in the preview, and/or `reset` its data to a fresh copy of the live app's (or the sample). */
  | { t: "bloomPreview"; rid: string; threadId: string; version?: number; reset?: boolean }
  /**
   * Every command's chat cards for the previewed draft (and the live version, when it's another one): forms, data
   * cards, and result cards from dry runs on the preview's data, rolled back (nothing is written). result: CardPreviews.
   */
  | { t: "bloomCards"; rid: string; threadId: string }
  /** The app's owner: who else may prompt, publish and restore data. */
  | { t: "appEditors"; rid: string; app: string; editors: string[] }
  /** Delete a bloomed app (uninstalls the app and its data; creator, admin, or server owner only). */
  | { t: "appDelete"; rid: string; app: string }
  /** Delete an app version from a blooming thread (creator, admin, or server owner only; the live version cannot be deleted). */
  | { t: "bloomDeleteVersion"; rid: string; threadId: string; version: number }
  /**
   * Owners and admins: move a built-in app (Feature requests) into a blooming thread in `channelId`, so it changes by
   * chatting like any app. Its data and History stay; version 1 is Bloom's own copy, live at once. result: { threadId }.
   */
  | { t: "bloomAdopt"; rid: string; app: string; channelId: string }
  /**
   * A bot's settings (docs/bots.md): who may talk to it (its owner), on/off (its owner, or a server owner/admin), or
   * one text channel it's in (`channelId` + `on`: its owner or a moderator adds; a moderator or its owner removes).
   * Answered with bloomResult { result: BotInfo }; everyone hears a "bot" event.
   */
  | { t: "botSettings"; rid: string; app: string; access?: BotAccess; disabled?: boolean; channelId?: string; on?: boolean }
  /** I opened this app's full view (bots with an appOpen trigger run for me; their replies go to our DM). */
  | { t: "appOpened"; app: string };

// Room → client
export type ServerEvent =
  | {
      t: "ready";
      you: string;
      categories: Category[];
      /** Text channels plus only the DMs you're in. */
      channels: Channel[];
      online: string[];
      /** How far you've read each channel/DM (seq), from any of your devices. Missing: never opened. */
      read: Record<string, number>;
      /** Unread messages that @mention you (or DMs you), per channel/DM, after your read position. */
      mentionCounts: Record<string, number>;
      /** Your notification levels where you picked one (missing: the server's, or "all" for DMs). */
      notify: Record<string, NotifyLevel>;
      /** Channels and DMs you muted: when each mute ends (ms) or MUTE_FOREVER. Only mutes still on. */
      mutes?: Record<string, number>;
      /** DMs you closed in your sidebar: the lastSeq each had when closed (see dmHidden). */
      dmClosed?: Record<string, number>;
      /** Your settings for this whole server (level, suppressing @everyone, a server mute). */
      serverNotify?: ServerNotify;
      /** The room's change counter right now; a client sends it back in its next hello (see hello's `rev`). */
      rev: number;
      /** The apps installed in this server (their manifests: the command registry is built from these). */
      apps: AppManifest[];
      /** My private cards (only ever on my own sockets). */
      privateCards: PrivateCard[];
      /** The blooming threads I may see: every one in a text channel, and private bots' threads in my own DMs. */
      blooming: BloomingThread[];
      /** Who may start blooming here. */
      bloomWho: BloomWho;
      /** The server's custom emoji and stickers (see emoji.ts). */
      emojis: CustomEmoji[];
      stickers: Sticker[];
      /** Lists I can see: every channel's pins I can see (once something was pinned), shared lists, and my own. */
      lists: ListInfo[];
      /** What's in my private lists: list id → message ids. Only ever on my own sockets. */
      privateItems: Record<string, string[]>;
      /** Which integrations are on here (docs/integrations.md). No keys or hints. */
      integrations: IntegrationSetting[];
      /** The server's bots (docs/bots.md): names, avatars, owners, who may talk to them, their channels. */
      bots?: BotInfo[];
    }
  /** Messages the client missed while away, oldest first. `more`: there were more than a page; fetch history. */
  /** reset: drop everything held for this channel first (the client's cursor didn't fit, or too much changed). */
  | { t: "backfill"; channelId: string; messages: Message[]; more: boolean; reset?: boolean }
  | { t: "msg"; m: Message }
  /** Messages the client already holds (at or before its cursor) that changed while it was away; replace them whole. */
  | { t: "changed"; channelId: string; messages: Message[] }
  /** Sent after the last backfill of a hello: the client is caught up with every channel. */
  | { t: "synced" }
  /**
   * Confirms the sender's own message (with the reply excerpt as the room stored it). `body`: present when the room
   * stored different text than was sent ("#general" becomes "<#id>"; see channels.ts).
   */
  /** forward: what the room stored for a forward (its files are copies with new keys), replacing the optimistic one. */
  | { t: "ack"; id: string; channelId: string; seq: number; ts: number; replyTo?: ReplyRef; body?: string; forward?: Forward }
  | { t: "history"; rid: string; channelId: string; messages: Message[]; more: boolean }
  /**
   * Someone added or removed a reaction. effect: a super reaction's effect, to play once (#87); live events only.
   * jumbo: `true` when the reaction is now jumbo (#714), a new one or one made jumbo: the user joins `jumbo[emoji]`, and
   * every device with the message on screen plays the thud once. Absent on a removal, which drops the user from both lists.
   * It is stored: history, `ready`, `changed` and `backfill` carry it in `Message.jumbo`.
   */
  | { t: "reaction"; channelId: string; messageId: string; emoji: string; userId: string; on: boolean; effect?: ReactionEffect; jumbo?: boolean }
  | { t: "deleted"; channelId: string; messageId: string }
  /** `mentions`: the edited message's mentions, recomputed (absent: none). */
  | { t: "edited"; channelId: string; messageId: string; body: string; editedAt: number; mentions?: string[] }
  /** Link previews for a message are ready (or changed after an edit; [] removes them). */
  | { t: "embeds"; channelId: string; messageId: string; embeds: Embed[] }
  /**
   * @Bloom's reply growing (about ten times a second): `body` is the whole text so far, not a delta, so a client that
   * joins mid-reply (or missed a frame) is right with the next one. `done`: finished; `body` is the stored text
   * (mentions and #channels encoded) and `mentions` its mentions. The message itself arrived first as a "msg" with
   * `streaming` set. A reconnecting client gets the finished text through hello's `changed`, like an edit.
   * `card`: with `done`, a card from an app tool Bloom used, shown under the text.
   */
  | { t: "stream"; channelId: string; messageId: string; body: string; done?: boolean; mentions?: string[]; card?: Card }
  /** A message's card changed: "+N more" results folded into it (see CARDS_PER_MINUTE). */
  | { t: "card"; channelId: string; messageId: string; card: Card }
  /** A message's attachments changed (one was removed, or marked private or not): the full new list. */
  | { t: "attachments"; channelId: string; messageId: string; attachments: Attachment[] }
  /** A thread under `parentId` (in `channelId`) got a reply. */
  | { t: "thread"; channelId: string; parentId: string; thread: ThreadSummary }
  /** `ts`: room time, for measuring how fast typing shows up. */
  | { t: "typing"; channelId: string; userId: string; ts: number }
  | { t: "presence"; userId: string; online: boolean }
  /** A member changed their profile (name or avatar): update them everywhere they're shown. */
  | { t: "memberUpdated"; member: MemberProfile }
  /** A channel was created or changed (renamed, new topic). */
  | { t: "channel"; channel: Channel; rid?: string }
  /** A category was created or renamed. */
  | { t: "category"; category: Category; rid?: string }
  /**
   * The whole layout changed at once (an import, a move, a deleted category): replace categories and channels.
   * Your own DMs are included, nobody else's.
   */
  | { t: "structure"; categories: Category[]; channels: Channel[]; rid?: string }
  /** A channel and everything in it is gone. Anyone looking at it moves somewhere else. */
  | { t: "channelDeleted"; channelId: string; rid?: string }
  /** A category is gone; a "structure" event with its channels, now uncategorised, comes with it. */
  | { t: "categoryDeleted"; categoryId: string; rid?: string }
  /** The server's custom emoji or stickers changed (uploaded, renamed, deleted, imported): the full new lists. */
  | { t: "customEmoji"; emojis: CustomEmoji[]; stickers: Sticker[] }
  /** The server's name or colour changed (in D1, through PATCH /api/servers/:id). */
  | { t: "server"; name: string; color: string }
  /** Someone's role changed (made or unmade admin, ownership transferred). */
  | { t: "role"; userId: string; role: Role }
  /** Someone left or was removed from the server. */
  | { t: "memberLeft"; userId: string }
  /**
   * The answer to a "messageRef": the message, its stream (`message.channelId`) and the channel or DM that stream is
   * in (`rootId`); null when it doesn't exist, was deleted, is a quiet line, or is somewhere I can't see.
   */
  | { t: "messageRef"; rid: string; messageId: string; message: Message | null; rootId: string | null }
  /** You read a channel/DM on another device (or this one): your read position moved to `seq`. */
  | { t: "read"; channelId: string; seq: number }
  /** You marked a channel/DM unread (from any of your devices): your read position is now `seq` (it may have moved
   * back), with `mentions` unread mentions after it. */
  | { t: "unread"; channelId: string; seq: number; mentions: number }
  /** Your notification level changed (from any of your devices); null: back to the server's. */
  | { t: "notifyPref"; channelId: string; level: NotifyLevel | null }
  /** You closed a DM (`seq`), or opened a closed one again (null), from any of your devices. */
  | { t: "dmClose"; channelId: string; seq: number | null }
  /** A mute changed (from any of your devices): `until` (ms or MUTE_FOREVER), null when unmuted. No channelId: the server. */
  | { t: "notifyMute"; channelId?: string; until: number | null }
  /** Your server-wide settings changed (from any of your devices). */
  | { t: "serverNotify"; settings: ServerNotify }
  /** `ts`: room time, so clients can estimate clock offset and measure delivery to others. */
  | { t: "pong"; n: number; ts: number }
  | { t: "error"; code: string; message: string; id?: string; rid?: string }
  // ---- Apps ----
  /** One of my private cards appeared or changed (e.g. its form was used). Only to my own sockets. */
  | { t: "privateCard"; card: PrivateCard }
  /** One of my private cards was dismissed (on any of my devices). */
  | { t: "privateCardGone"; id: string }
  /** A record changed (to sockets subscribed to its app or collection). `record` absent: it was deleted. */
  | { t: "appChange"; app: string; collection: string; id: string; record?: AppRecord }
  | { t: "appRecords"; rid: string; app: string; collection: string; records: AppRecord[] }
  | { t: "appHistory"; rid: string; app: string; events: AppEvent[]; more: boolean }
  /** The answer to appCall / appRestore / appPostCard. `errors`: a message per input that's wrong. */
  | { t: "appResult"; rid: string; ok: boolean; result?: unknown; message?: string; errors?: Record<string, string> }
  /** The answer to a command. `open`: the client should open this app's full view (a bare app). */
  | { t: "commandResult"; rid: string; ok: boolean; message?: string }
  // ---- Lists ----
  /** A list appeared or changed (renamed, new emoji, its count). To everyone who can see it. */
  | { t: "list"; list: ListInfo; rid?: string }
  | { t: "listDeleted"; listId: string; rid?: string }
  /**
   * A message went into a list or came out (to everyone who can see the list). `lists`: the message's shared lists
   * now (absent for a private list). `list`: the list as it is now (its count).
   */
  | { t: "listItem"; item: ListItem; on: boolean; list: ListInfo; lists?: string[]; rid?: string }
  /** A page of a list: its items (newest added first) and their messages (those I can see), and whether there's more. */
  | { t: "listItems"; rid: string; listId: string; items: ListItem[]; messages: Message[]; more: boolean }
  | { t: "searchResults"; rid: string; q: string; hits: SearchHit[]; messages: Message[]; more: boolean }
  /** The installed apps changed (one was published, rolled back, or its editors changed): the whole list. */
  | { t: "apps"; apps: AppManifest[] }
  | { t: "integrationResult"; rid: string; ok: true; data: unknown }
  | { t: "integrationResult"; rid: string; ok: false; code: string; message: string }
  /** The server's integration settings changed (on/off, a key added or removed). No keys or hints in it. */
  | { t: "integrations"; integrations: IntegrationSetting[] }
  // ---- Blooming ----
  /** A blooming thread was started or changed (status, progress, a new draft, published, locked). */
  | { t: "blooming"; thread: BloomingThread }
  /** A blooming thread was deleted. */
  | { t: "bloomingDeleted"; threadId: string }
  | { t: "bloomVersions"; rid: string; threadId: string; versions: AppVersion[] }
  | { t: "bloomResult"; rid: string; ok: boolean; message?: string; result?: unknown }
  /** Who may start blooming changed (an owner's setting). */
  | { t: "bloomWho"; who: BloomWho }
  /** A bot was added or its settings changed (on/off, access, channels). */
  | { t: "bot"; bot: BotInfo };
